import { useState } from "react";
import { Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

/**
 * Social share buttons for a blog article.
 *
 * Renders WhatsApp, Twitter/X, Facebook, LinkedIn + "copy link".
 * Uses the native Web Share API on mobile if available (one button only).
 * `compact` variant renders inline (under the header). Default is the wide
 * card variant used at the end of the article.
 */
const SOCIAL_LINKS = {
  whatsapp: (url, text) => `https://api.whatsapp.com/send?text=${encodeURIComponent(`${text} ${url}`)}`,
  twitter: (url, text) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  facebook: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  linkedin: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
};

const ShareIcon = ({ name, className = "w-4 h-4" }) => {
  switch (name) {
    case "whatsapp":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
          <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.9-.4-1.7-.9-2.4-1.6-.7-.7-1.2-1.5-1.6-2.4-.1-.2 0-.4.1-.5.1-.1.3-.3.4-.4.1-.2.2-.3.3-.5.1-.2 0-.3 0-.5l-.7-2.1c-.2-.5-.4-.5-.7-.5h-.4c-.2 0-.5.1-.7.3-.3.3-.9.9-.9 2.2 0 1.3.9 2.6 1 2.7.1.2 1.8 2.7 4.4 3.8.6.3 1.1.4 1.5.5.6.2 1.2.2 1.6.1.5-.1 1.6-.7 1.8-1.3.2-.6.2-1.2.1-1.3 0-.1-.2-.2-.5-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.3c1.5.8 3.3 1.3 5.2 1.3 5.5 0 10-4.5 10-10S17.5 2 12 2z" />
        </svg>
      );
    case "twitter":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "facebook":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
          <path d="M22 12c0-5.5-4.5-10-10-10S2 6.5 2 12c0 5 3.7 9.1 8.4 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7C18.3 21.1 22 17 22 12z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM8.3 18.3H5.7V9.7h2.6v8.6zM7 8.6c-.8 0-1.5-.7-1.5-1.5S6.2 5.6 7 5.6s1.5.7 1.5 1.5S7.8 8.6 7 8.6zm11.3 9.7h-2.6v-4.3c0-1 0-2.3-1.4-2.3s-1.6 1.1-1.6 2.2v4.4h-2.6V9.7h2.5V11c.4-.7 1.3-1.5 2.6-1.5 2.7 0 3.2 1.8 3.2 4.2v4.6z" />
        </svg>
      );
    default:
      return null;
  }
};

const ShareButtons = ({ title, url, compact = false }) => {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const fullUrl = typeof window !== "undefined" && url.startsWith("/") ? `${window.location.origin}${url}` : url;
  const shareText = title;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success(t("share.copied"));
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast.error(t("share.copyError"));
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: shareText, url: fullUrl });
    } catch (_) {
      /* user cancelled */
    }
  };

  const hasNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const channels = [
    { id: "whatsapp", href: SOCIAL_LINKS.whatsapp(fullUrl, shareText), label: "WhatsApp" },
    { id: "twitter", href: SOCIAL_LINKS.twitter(fullUrl, shareText), label: "X / Twitter" },
    { id: "facebook", href: SOCIAL_LINKS.facebook(fullUrl), label: "Facebook" },
    { id: "linkedin", href: SOCIAL_LINKS.linkedin(fullUrl), label: "LinkedIn" },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="share-buttons-compact">
        <span className="text-[10px] tracking-[0.3em] uppercase text-charcoal/45 mr-1">{t("share.label")}</span>
        {channels.map((c) => (
          <a
            key={c.id}
            href={c.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t("share.aria")} ${c.label}`}
            data-testid={`share-${c.id}-compact`}
            className="w-8 h-8 rounded-full border border-charcoal/15 text-charcoal/65 hover:border-terracotta hover:text-terracotta transition-colors flex items-center justify-center"
          >
            <ShareIcon name={c.id} />
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopy}
          aria-label={t("share.copy")}
          data-testid="share-copy-compact"
          className="w-8 h-8 rounded-full border border-charcoal/15 text-charcoal/65 hover:border-terracotta hover:text-terracotta transition-colors flex items-center justify-center"
        >
          {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  return (
    <div
      className="bg-cream/60 border border-sand/70 rounded-3xl p-7 md:p-9"
      data-testid="share-buttons"
    >
      <p className="text-[11px] tracking-[0.3em] uppercase text-sage mb-3">{t("share.eyebrow")}</p>
      <h3 className="font-serif text-2xl md:text-3xl text-charcoal leading-snug">
        {t("share.title")}
      </h3>
      <p className="mt-3 text-charcoal/65 text-[15px] leading-relaxed">
        {t("share.subtitle")}
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        {channels.map((c) => (
          <a
            key={c.id}
            href={c.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`share-${c.id}`}
            className="inline-flex items-center gap-2 bg-ivory border border-sand text-charcoal rounded-full px-4 py-2.5 text-[13px] hover:border-terracotta hover:text-terracotta transition-colors"
          >
            <ShareIcon name={c.id} className="w-4 h-4" /> {c.label}
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopy}
          data-testid="share-copy"
          className="inline-flex items-center gap-2 bg-ivory border border-sand text-charcoal rounded-full px-4 py-2.5 text-[13px] hover:border-terracotta hover:text-terracotta transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-terracotta" /> : <Link2 className="w-4 h-4" />}
          {copied ? t("share.copied") : t("share.copy")}
        </button>
        {hasNativeShare && (
          <button
            type="button"
            onClick={handleNativeShare}
            data-testid="share-native"
            className="inline-flex items-center gap-2 bg-charcoal text-ivory rounded-full px-4 py-2.5 text-[13px] hover:bg-terracotta transition-colors sm:hidden"
          >
            {t("share.more")}
          </button>
        )}
      </div>
    </div>
  );
};

export default ShareButtons;
